import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface VolcesRealmanAvatarImitatorV2vParams {
  /**
   * 人物图片URL，必填参数。用于指定要生成数字人的人物形象
   */
  image_url: string;
  /**
   * 驱动视频信息，必填参数。包含驱动视频的URL和存储类型
   */
  driving_video_info: {
    /**
     * 驱动视频URL，必填参数。用于指定驱动数字人动作的视频
     */
    video_url: string;
    /**
     * 存储类型，必填参数。必须为0
     * @values 0
     */
    store_type: 0;
  };
  /**
   * 随机种子，可选参数。必须为-1或正整数，用于控制生成结果的随机性
   */
  seed?: number;
  /**
   * 分辨率，可选参数。指定输出视频的分辨率
   */
  resolution?: string;
  /**
   * 帧率，可选参数。必须为正整数，推荐值为25或30
   */
  fps?: number;
  /**
   * 比特率，可选参数。必须为正整数，用于控制视频质量
   */
  bitrate?: number;
  /**
   * 水印信息，可选参数
   */
  logo_info?: {
    /**
     * 是否添加水印
     */
    add_logo?: boolean;
    /**
     * 水印位置
     */
    position?: number;
    /**
     * 水印语言
     */
    language?: number;
    /**
     * 水印透明度
     */
    opacity?: number;
    /**
     * 水印文字内容
     */
    logo_text_content?: string;
  };
  /**
   * 是否返回视频URL，可选参数。默认为true
   */
  return_url?: boolean;
}

export interface VolcesRealmanAvatarImitatorV2vResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * volces_realman_avatar_imitator_v2v
 *
 * API Documentation: https://www.volcengine.com/docs/86081/1804525
 * 
 * Volces RealMan Avatar Imitator V2V 视频生成实现（火山引擎）。支持视频到视频的数字人模仿生成，根据输入的人物图片和驱动视频生成数字人模仿视频。
 *
 * @example
 * ```typescript
 * import { initSeacloud, volcesRealmanAvatarImitatorV2v } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await volcesRealmanAvatarImitatorV2v({
 *   // 参数见 VolcesRealmanAvatarImitatorV2vParams 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 VolcesRealmanAvatarImitatorV2vParams 接口定义
 * @returns 生成的资源列表
 */
export async function volcesRealmanAvatarImitatorV2v(
  params: VolcesRealmanAvatarImitatorV2vParams,
): Promise<VolcesRealmanAvatarImitatorV2vResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 5000,
    maxAttempts: 120,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'volces_realman_avatar_imitator_v2v',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: VolcesRealmanAvatarImitatorV2vResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
